import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { extractErrorMessage } from '../utils/errorHandler';
import './OfferList.css';
import { ArchivedProduct } from '../types';
import { formatDateTime } from '../utils/dates';

const ArchivedProducts: React.FC = () => {
  const navigate = useNavigate();
  const [archived, setArchived] = useState<ArchivedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [nameFilter, setNameFilter] = useState('');

  useEffect(() => {
    fetchArchived();
  }, []);

  const fetchArchived = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/products/archive');
      setArchived(response.data);
    } catch (err: any) {
      console.error('Error fetching archived products:', err);
      setError(`Nie udało się wczytać archiwum: ${extractErrorMessage(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (archiveId: number, name: string) => {
    if (!window.confirm(`Czy przywrócić produkt "${name}"?`)) {
      return;
    }

    setRestoringId(archiveId);
    setError('');
    try {
      await axios.post(`/api/products/${archiveId}/restore`);
      // The backend retires the row (change_type -> 'restored'); drop it locally
      // too so the list updates without a refetch.
      setArchived(prev => prev.filter(a => a.id !== archiveId));
      setSuccess(`Produkt "${name}" został przywrócony`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err: any) {
      console.error('Error restoring product:', err);
      setError(`Nie udało się przywrócić produktu: ${extractErrorMessage(err)}`);
    } finally {
      setRestoringId(null);
    }
  };

  const filtered = useMemo(() => {
    const needle = nameFilter.trim().toLocaleLowerCase('pl');
    if (!needle) return archived;
    return archived.filter(a =>
      (a.name || '').toLocaleLowerCase('pl').includes(needle) ||
      (a.brand || '').toLocaleLowerCase('pl').includes(needle)
    );
  }, [archived, nameFilter]);

  const formatPrice = (price?: number | null) => {
    if (price === null || price === undefined) return 'Brak ceny';
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
    }).format(price);
  };

  if (loading) {
    return <div className="loading">Ładowanie archiwum...</div>;
  }

  return (
    <div className="container">
      <div className="card template-list">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h1>Archiwum Produktów</h1>
          <button className="btn btn-secondary" onClick={() => navigate('/')}>
            Wróć do produktów
          </button>
        </div>

        <div className="card-body">
          {error && <div className="alert alert-danger">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <p className="text-muted">
            Usunięte produkty nie są kasowane z bazy — trafiają tutaj i można je przywrócić.
          </p>

          {archived.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">Archiwum jest puste.</p>
            </div>
          ) : (
            <>
              <div className="offer-filters">
                <div className="offer-filter-group">
                  <label htmlFor="archive-filter-name">Nazwa lub marka</label>
                  <input
                    id="archive-filter-name"
                    type="text"
                    className="offer-filter-input"
                    placeholder="Szukaj w archiwum..."
                    value={nameFilter}
                    onChange={e => setNameFilter(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-outline btn-sm offer-filter-clear"
                  onClick={() => setNameFilter('')}
                  disabled={nameFilter === ''}
                >
                  Wyczyść filtr
                </button>
              </div>

              <div className="offer-filter-count">
                {nameFilter
                  ? `Znaleziono ${filtered.length} z ${archived.length} produktów`
                  : `Zarchiwizowane produkty: ${archived.length}`}
              </div>

              {filtered.length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-muted">Żaden produkt nie pasuje do filtru.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Nazwa</th>
                        <th>Marka</th>
                        <th>Kraj</th>
                        <th>Cena</th>
                        <th>Data usunięcia</th>
                        <th>Akcje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map(item => (
                        <tr key={item.id}>
                          <td>{item.name}</td>
                          <td>{item.brand || '-'}</td>
                          <td>{item.country || '-'}</td>
                          <td>{formatPrice(item.price_net)}</td>
                          <td>
                            {item.archived_at
                              ? formatDateTime(item.archived_at)
                              : '-'}
                          </td>
                          <td>
                            <div className="btn-group">
                              <button
                                className="btn btn-outline-primary"
                                onClick={() => handleRestore(item.id, item.name)}
                                disabled={restoringId === item.id}
                              >
                                {restoringId === item.id ? 'Przywracanie...' : 'Przywróć'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArchivedProducts;
